/**
 * Résultats bruts d'UNE épreuve, quel que soit son type : l'onglet d'une
 * épreuve dans la fiche candidat, sur les deux portails.
 *
 * Présentationnel : chaque portail lit l'évaluation avec SA query (org-scopée
 * côté client, `*SuperAdmin` côté admin) et la passe ici, avec la liste des
 * rapports proposés.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui";
import {
  AutodiagResults,
  Copsoq3Results,
  EgogrammeResults,
  EntStructResults,
  FitCulturelResults,
  InbasketResults,
  PamiResultsEcharts,
  RapportsMenu,
  type RapportPropose,
} from "@repo/results";
import { versionCopsoq3, versionCopsoq3DeDonnees } from "@repo/utils";
import type { FunctionReturnType } from "convex/server";
import type { api } from "../../../../convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "../../../../convex/schema";
import { PYRAMIDE_FIT_CULTUREL } from "../../../../convex/referentiel/qsort";
import referentielEnedis from "../../../../referentiel_enedis.json";

/** Ce que renvoient les deux queries de lecture d'une évaluation. */
export type ResultatEvaluation =
  | NonNullable<FunctionReturnType<typeof api.evaluations.obtenirEvaluationPourAdmin>>
  | NonNullable<FunctionReturnType<typeof api.evaluations.obtenirEvaluationSuperAdmin>>;

type Props = {
  resultat: ResultatEvaluation;
  /** Rapports proposés pour cette évaluation (`undefined` = chargement). */
  rapportsDisponibles: RapportPropose[] | undefined;
  /** Jeton d'auth et URL Convex de l'app, pour télécharger les rapports. */
  token: string | null;
  convexUrl: string;
  /** Afficher la version du questionnaire COPSOQ III (portail admin). */
  afficherVersionCopsoq3?: boolean;
};

export function ResultatsEpreuve({
  resultat,
  rapportsDisponibles,
  token,
  convexUrl,
  afficherVersionCopsoq3 = false,
}: Props) {
  const { evaluation, tempsPasse } = resultat;

  const candidatInfo = {
    prenom: evaluation.prenomCandidat,
    nom: evaluation.nomCandidat,
    email: evaluation.emailCandidat,
    dateDebut: evaluation.dateDebut,
    dateFin: evaluation.dateFin,
    tempsPasse,
  };

  const menuRapports = (
    <RapportsMenu
      evaluationId={evaluation._id}
      rapports={rapportsDisponibles}
      candidatInfo={candidatInfo}
      token={token}
      convexUrl={convexUrl}
    />
  );

  const renderResults = () => {
    const { donnees } = evaluation;

    if (donnees.type === TypeEvalEnum.INBASKET) {
      return (
        <InbasketResults
          donnees={donnees}
          candidatInfo={candidatInfo}
          resultatsRacine={evaluation.resultats}
        />
      );
    }

    if (donnees.type === TypeEvalEnum.FORM) {
      // donneesFormValidator est un objet unique, PAS une union d'objets : TypeScript
      // ne peut donc affiner ni donnees.template ni donnees.reponses vers les types
      // propres à un template par simple test d'égalité. D'où le cast vers unknown,
      // puis vers la forme de prop attendue.
      if (donnees.template === TemplateFormEnum.AUTODIAG) {
        type AutodiagShape = {
          type: "form";
          template: "autodiag";
          reponses: Record<
            string,
            {
              name: string;
              label: string;
              reponse: string;
              titre?: string;
              sous_questions?: {
                name: string;
                label: string;
                reponse: string;
              }[];
            }
          >;
        };
        return (
          <AutodiagResults
            donnees={donnees as unknown as AutodiagShape}
            referentiel={referentielEnedis}
            candidatInfo={candidatInfo}
          />
        );
      }
      if (donnees.template === TemplateFormEnum.EGOGRAMME) {
        type EgogrammeShape = {
          type: "form";
          template: "egogramme";
          reponses: Record<
            string,
            { name: string; label: string; profil: string; response?: boolean }
          >;
        };
        return (
          <EgogrammeResults
            donnees={donnees as unknown as EgogrammeShape}
            candidatInfo={candidatInfo}
          />
        );
      }
      if (donnees.template === TemplateFormEnum.COPSOQ3) {
        type ReponseNonScoree = {
          name: string;
          rubrique?: string;
          question: string;
          reponse: string | number | string[];
        };
        type Copsoq3Shape = {
          type: "form";
          template: "copsoq3";
          reponses: Record<
            string,
            {
              name: string;
              question: string;
              echelle: string;
              domaine: string;
              reponse: string;
              score: number;
            }
          >;
          profil?: ReponseNonScoree[];
          sante?: ReponseNonScoree[];
        };
        return (
          <Copsoq3Results
            donnees={donnees as unknown as Copsoq3Shape}
            candidatInfo={candidatInfo}
            version={
              afficherVersionCopsoq3
                ? versionCopsoq3(versionCopsoq3DeDonnees(donnees)).libelle
                : undefined
            }
          />
        );
      }
      if (donnees.template === TemplateFormEnum.FIT_CULTUREL) {
        type FitCulturelShape = {
          type: "form";
          template: "pc-fit";
          reponses: Record<
            string,
            { name: string; label: string; niveau: number; score?: number }
          >;
        };
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              {menuRapports}
            </div>
            <FitCulturelResults
              donnees={donnees as unknown as FitCulturelShape}
              candidatInfo={candidatInfo}
              nbNiveaux={PYRAMIDE_FIT_CULTUREL.length}
            />
          </div>
        );
      }
      if (donnees.template === TemplateFormEnum.PAMI) {
        return (
          <div className="space-y-4">
            <div className="flex justify-end">
              {menuRapports}
            </div>
            <PamiResultsEcharts
              donnees={donnees}
              candidatInfo={candidatInfo}
              resultatsRacine={evaluation.resultats}
            />
          </div>
        );
      }
      return <div>Type de formulaire inconnu</div>;
    }

    if (donnees.type === TypeEvalEnum.ENT_STRUCT) {
      return (
        <div className="space-y-4">
          <div className="flex justify-end">
            {menuRapports}
          </div>
          <EntStructResults
            donnees={donnees}
            candidatInfo={candidatInfo}
            resultatsRacine={evaluation.resultats}
            notationsParModele={
              "notationsParModele" in evaluation
                ? evaluation.notationsParModele
                : undefined
            }
          />
        </div>
      );
    }

    return <div>Type d'évaluation non supporté</div>;
  };

  return (
    <div className="space-y-6">
      {renderResults()}

      {/* Retours d'expérience */}
      {evaluation.retoursExperience && (
        <Card>
          <CardHeader>
            <CardTitle>Retours d'expérience</CardTitle>
            <CardDescription>
              Commentaires du candidat sur l'évaluation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(evaluation.retoursExperience).map(
                ([key, value]) => (
                  <div key={key} className="p-4 bg-muted rounded-surface">
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {key}
                    </p>
                    <p className="whitespace-pre-wrap">{value}</p>
                  </div>
                ),
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
